import { rnd1 } from "../../Game";
import { WrappedArray } from "../../Utils/Collections/WrapperArray";
import { MathUtils } from "../../Utils/MathUtils";
import { NumValBase, RndNumVal } from "../../Utils/NumberValue";
import { DefenderSettings } from "../Defenders/DefenderSettings";
import { HUD } from "../HUD/HUD";
import { TutorialSettings } from "../HUD/Tutorial";
import { Ball } from "../Player/Ball";
import { Stadium } from "../Stadium/Stadium";
import { TargetScalingSettings } from "../Targets/TargetScaling";
import { TargetSettingsBase } from "../Targets/TargetSettings";
import { eTargetType } from "../Targets/TargetType";
import { RoundManager } from "./RoundManager";
import { SceneSetupData } from "./SceneSetupSelector";



export class RoundSetup {
    private static readonly MIN_KICKOFF_DIS = 14;
    private static readonly MAX_KICKOFF_DIS = 21;

    /**
     * Pravdepodobnost zobrazeni obrancu pri nejnizsi obtiznosti
     */
    static readonly DEFENDERS_DEF_MIN_DIF_PROBABILITY = 0;
    /**
     * Pravdepodobnost zobrazeni obrancu pri nejvyssi obtiznosti
     */
    static readonly DEFENDERS_DEF_MAX_DIF_PROBABILITY = 75;
    /**
     * Pravdepodobnost zobrazeni bonusove terce pro aktivaci fireballu
     */
    static readonly BONUS_TARGETS_PROBABILITY = 75;
    /**
     * Pravdepodobnost, ze hlavni terc bude menit svoji velikost pri nejnizsi obtiznosti
     */
    static readonly MAIN_TARGET_SCALING_MIN_DIF_PROBABILITY = 0;
    /**
     * Pravdepodobnost, ze hlavni terc bude menit svoji velikost pri nejvyssi obtiznosti
     */
    static readonly MAIN_TARGET_SCALING_MAX_DIF_PROBABILITY = 50;

    /**
     * Zakladni nastaveni obrancu
     */
    private static _defDefenderSettings: DefenderSettings[];
    /**
     * Nastaveni zvetsovani hlavniho terce
     */
    private static _mainTargetScalings: TargetScalingSettings[];
    /**
     * (RO) Jedinecny identifikator nastaveni
     */
    get setupUID(): number { return this._setupUID; }
    private _setupUID: number;
    /**
     * (RO) True, pokud nastaveni bylo horizontalne prevraceno
     */
    get flippedHorizontally(): boolean { return this._flippedHorizontally; }
    private _flippedHorizontally: boolean;
    /**
     * (RO) Pozice mice pro vykop
     */
    get kickOffPos(): pc.Vec3 { return this._kickOffPos; }
    private _kickOffPos = new pc.Vec3();
    /**
     * (RO) Vzdalenost brany (horizontalniho stredu) od pozice vykopu
     */
    get goalDistance(): number { return this._goalDistance; }
    private _goalDistance: number;
    /**
     * (RO) Uhel z kick off pozice do stredu brany v radianech
     */
    get goalCenterAngle(): number { return this._goalCenterAngle; }
    private _goalCenterAngle: number;
    /**
     * (RO) Uhel z kick off pozice k levemu okraji brany v radianech
     */
    get goalLSideAngle(): number { return this._goalLSideAngle; }
    private _goalLSideAngle: number;
    /**
     * (RO) Uhel z kick off pozice k pravemu okraji brany v radianech
     */
    get goalRSideAngle(): number { return this._goalRSideAngle; }
    private _goalRSideAngle: number;
    /**
     * (RO) True, pokud nema byt brankar
     */
    get noGoalie(): boolean { return this._noGolie; }
    private _noGolie: boolean;
    /**
     * (RO) Vychozi pozice brankare na ose X
     */
    get goalieX(): number { return this._goalieX; }
    private _goalieX: number;
    /**
     * (RO) Nastaveni tercu
     */
    get targets(): WrappedArray<TargetSettingsBase> { return this._targets; }
    private _targets: WrappedArray<TargetSettingsBase>;
    /**
     * (RO) Nastaveni zmeny velikosti hlavniho terce.
     */
    get mainTargetScaling(): TargetScalingSettings { return this._mainTargetScaling; }
    private _mainTargetScaling: TargetScalingSettings;
    /**
     * (RO) True, pokud se nemaji v tomto kole zobrazit bonusove terce
     */
    get bonusTargetsDisabled(): boolean { return this._bnsTargetsDisabled; }
    private _bnsTargetsDisabled: boolean;
    /**
     * (RO) Nastaveni blokovani obranci
     */
    get defenderSettings(): DefenderSettings { return this._defenderSettings; }
    private _defenderSettings: DefenderSettings;
    /**
     * (RO) Nastaveni tutorialu
     */
    get tutorialSettings(): TutorialSettings { return this._tutorialSettings; }
    private _tutorialSettings: TutorialSettings;
    /**
     * (RO) Maska aktivnich zon
     */
    get actZoneMask(): number { return this._actZoneMask; }
    private _actZoneMask: number = 0;
    /**
     * (RO) Indexy aktivnich zon
     */
    get actZones(): number[] { return this._actZones; }
    private _actZones: number[] = [];

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        if (!RoundSetup._defDefenderSettings) {
            RoundSetup._defDefenderSettings = [
                { sizeRatio: 0.35, distanceRatio: new RndNumVal(0.4, 0.6, 4), pos: -1, goaliePos: 0.7, minDif: 0.2 },
                { sizeRatio: 0.35, distanceRatio: new RndNumVal(0.4, 0.6, 4), pos: 0, goaliePos: 0.5, minDif: 0.2 },
                { sizeRatio: 0.35, distanceRatio: new RndNumVal(0.4, 0.6, 4), pos: 1, goaliePos: 0.3, minDif: 0.2 },
            ];

            RoundSetup._mainTargetScalings = [
                new TargetScalingSettings(0.5, 0, 1000, pc.SineIn, 0, 1500, pc.SineOut)
            ];
        }

        this._targets = new WrappedArray<TargetSettingsBase>();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(data: SceneSetupData, flipHorizontally: boolean): void {
        let rndMng = RoundManager.instance;

        //HUD.instance.debugOutput.addEntry("SCENE UID: " + data.uid);

        this._setupUID = data.uid;
        this._flippedHorizontally = flipHorizontally;

        // Nastavit pozici vykopnuti        
        let angle = NumValBase.getValue(data.kickOffAngle);

        if (flipHorizontally)
            angle = 90 + (90 - angle);

        let dis: number;

        if (data.kickOffDis != undefined) {
            dis = NumValBase.getValue(data.kickOffDis);
        }
        else {
            dis = RoundSetup.MIN_KICKOFF_DIS + (RoundSetup.MAX_KICKOFF_DIS - RoundSetup.MIN_KICKOFF_DIS) * RoundManager.instance.roundDifficultyIncRatio;
        }

        // Maska aktivnich zon
        let actZoneMask = (angle > 80 && angle < 100 ? 2 : angle <= 80 ? 3 : 6);

        angle *= pc.math.DEG_TO_RAD;
        this._goalCenterAngle = angle;
        this._kickOffPos.set(MathUtils.roundToXDecPlaces(Stadium.CENTER_X + Math.cos(angle) * dis, 4), Ball.RADIUS, MathUtils.roundToXDecPlaces(Stadium.END_Z + Math.sin(angle) * dis, 4));

        // Vzdalenost do stredu brany
        this._goalDistance = dis;

        // Uhel k leve a prave strane brany od kick off pozice
        this._goalLSideAngle = Math.atan2(Stadium.END_Z - this._kickOffPos.z, (Stadium.CENTER_X - Stadium.GOAL_WIDTH / 2) - this._kickOffPos.x);
        this._goalRSideAngle = Math.atan2(Stadium.END_Z - this._kickOffPos.z, (Stadium.CENTER_X + Stadium.GOAL_WIDTH / 2) - this._kickOffPos.x);

        // Obranci
        let defSet = data.defDefenderSettingsId != undefined ? RoundSetup._defDefenderSettings[data.defDefenderSettingsId] : data.defenderSettings;

        this._defenderSettings = undefined;

        if (defSet) {
            let rndDif = rndMng.roundDifficultyIncRatio;

            if (defSet.minDif == undefined || defSet.minDif <= rndDif) {
                let minDifProb = defSet.minDifProbability || RoundSetup.DEFENDERS_DEF_MIN_DIF_PROBABILITY;
                let maxDifProb = defSet.maxDifProbability || RoundSetup.DEFENDERS_DEF_MAX_DIF_PROBABILITY;

                if (maxDifProb < minDifProb)
                    maxDifProb = minDifProb;

                if (rnd1.randomIntIn(1, 100) <= minDifProb + (maxDifProb - minDifProb) * rndDif)
                    this._defenderSettings = defSet;
            }
        }

        // Pozice brankare
        let goaliePos = (this._defenderSettings ? this._defenderSettings.goaliePos : data.goaliePos);

        this._noGolie = false;

        if (goaliePos == undefined) {
            this._goalieX = Stadium.CENTER_X;
        }
        else {
            let pos = NumValBase.getValue(goaliePos);

            if (pos >= 0 && pos <= 1) {
                let halfRange = (Stadium.GOAL_WIDTH / 2) - 0.5;

                if (flipHorizontally)
                    pos = 1 - pos;

                this._goalieX = (Stadium.CENTER_X - halfRange) + (halfRange * 2) * pos;
            }
            else {
                // Kolo bude bez brankare
                this._noGolie = true;
            }

        }

        // Terce
        this._targets.clear(false);

        if (Array.isArray(data.targets)) {
            data.targets.forEach((target: TargetSettingsBase): void => {
                this._targets.add(target);
            });
        }
        else {
            this._targets.add(data.targets);
        }

        // Zmena velikosti hlavniho terce
        this._mainTargetScaling = undefined;

        /*if (data.enableMainTargetScaling) {
            if (rnd1.randomIntIn(1, 100) <= RoundSetup.MAIN_TARGET_SCALING_MIN_DIF_PROBABILITY + (RoundSetup.MAIN_TARGET_SCALING_MAX_DIF_PROBABILITY - RoundSetup.MAIN_TARGET_SCALING_MIN_DIF_PROBABILITY) * rndMng.roundDifficultyIncRatio) {
                this._mainTargetScaling = RoundSetup._mainTargetScalings[0];
            }
        }*/

        // Tutorial
        this._tutorialSettings = data.tutorial;

        // Indexy aktivnich zon
        if (this._actZoneMask != actZoneMask) {
            this._actZoneMask = actZoneMask;
            this._actZones.length = 0;

            for (let zoneId = 0; zoneId < 3; zoneId++) {
                if ((this._actZoneMask & (1 << zoneId)) != 0)
                    this._actZones.push(zoneId);
            }
        }
    }
}